import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import {
    Lightning,
    MagnifyingGlass,
    MapPin,
    Briefcase,
    ArrowRight,
    UserCircle,
    Buildings,
    Funnel,
    X,
    BookmarkSimple,
    Check,
} from "@phosphor-icons/react";
import AutocompleteInput from "@/components/AutocompleteInput";
import { LOCATION_SUGGESTIONS, INDUSTRY_TYPE_OPTIONS } from "@/lib/suggestions";
import { toast } from "sonner";
import useSeo from "@/lib/useSeo";

const TYPES = ["", "Full-time", "Part-time", "Contract", "Internship"];
const SENIORITIES = ["", "Junior", "Mid", "Senior", "Lead", "Director"];
const WORK_MODES = ["", "Onsite", "Hybrid", "Remote"];
const INDUSTRIES = ["", ...INDUSTRY_TYPE_OPTIONS];

// Public jobs board — proper search results page.
// URL query params keep state deep-linkable.
export default function PublicJobsBoard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [jobs, setJobs] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const q = params.get("q") || "";
    const location = params.get("location") || "";
    const employment_type = params.get("type") || "";
    const seniority = params.get("seniority") || "";
    const work_mode = params.get("work_mode") || "";
    const industry = params.get("industry") || "";

    const [draftQ, setDraftQ] = useState(q);
    const [draftLoc, setDraftLoc] = useState(location);

    // SEO — each filter combo (e.g. "Senior · Bengaluru") becomes its own
    // indexable landing page for Google Jobs vertical search.
    const seoTitleBits = [];
    if (seniority) seoTitleBits.push(seniority);
    if (q) seoTitleBits.push(q);
    seoTitleBits.push("Jobs");
    if (location) seoTitleBits.push("in " + location);
    seoTitleBits.push("· India · NaukriDaddy");
    useSeo({
        title: seoTitleBits.join(" "),
        description: `Live${seniority ? " " + seniority : ""} ${q || "roles"}${location ? " in " + location : " across India"} on NaukriDaddy — India's most curated agency job board. Apply once, get matched everywhere.`,
        canonical: `https://naukridaddy.com/jobs${window.location.search || ""}`,
        // V1.5.8.49 — carry the default OG image so LinkedIn/WhatsApp
        // unfurls render the wordmark card instead of an empty preview.
        ogImage: "https://naukridaddy.com/og-default.png",
        jsonLd: (jobs && jobs.length > 0) ? {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": jobs.slice(0, 12).map((r, i) => ({
                "@type": "ListItem",
                "position": i + 1,
                "item": {
                    "@type": "JobPosting",
                    "title": r.title,
                    "hiringOrganization": {
                        "@type": "Organization",
                        "name": r.client?.name || "A NaukriDaddy client",
                    },
                    "jobLocation": {
                        "@type": "Place",
                        "address": { "@type": "PostalAddress", "addressLocality": r.location || "India", "addressCountry": "IN" },
                    },
                    "employmentType": (r.employment_type || "FULL_TIME").toUpperCase().replace("-", "_"),
                    "datePosted": r.created_at,
                    "url": `https://naukridaddy.com/jobs/${r.id}`,
                },
            })),
        } : null,
    });

    const load = useCallback(() => {
        const payload = { limit: 50 };
        if (q) payload.q = q;
        if (location) payload.location = location;
        if (industry) payload.industry = industry;
        if (work_mode) payload.work_mode = work_mode;
        setJobs(null);
        api
            .get("/public/jobs", { params: payload })
            .then((r) => {
                let items = r.data.items || [];
                if (employment_type)
                    items = items.filter((j) => j.employment_type === employment_type);
                if (seniority) items = items.filter((j) => j.seniority === seniority);
                setJobs(items);
            })
            .catch(() => setJobs([]));
    }, [q, location, employment_type, seniority, work_mode, industry]);

    useEffect(() => {
        load();
    }, [load]);

    const submitSearch = (e) => {
        e?.preventDefault();
        const next = new URLSearchParams(params);
        if (draftQ) next.set("q", draftQ);
        else next.delete("q");
        if (draftLoc) next.set("location", draftLoc);
        else next.delete("location");
        setParams(next);
    };

    const setFilter = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value);
        else next.delete(key);
        setParams(next);
    };

    const clearAll = () => {
        setDraftQ("");
        setDraftLoc("");
        setParams(new URLSearchParams());
    };

    const activeFilters =
        [q, location, employment_type, seniority, industry].filter(Boolean).length;

    const [saving, setSaving] = useState(false);
    const saveSearch = async () => {
        if (!user || user.role !== "candidate") {
            toast.info("Sign in as a candidate to save searches.");
            return;
        }
        if (activeFilters === 0) {
            toast.info("Apply at least one filter, then save.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/me/candidate/saved-searches", {
                query: q, location, employment_type, seniority, industry,
            });
            toast.success("Search saved — you'll find it in your candidate portal.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save search.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-ink">
            {/* V1.5.8.48 — dynamic query-aware SEO comes from useSeo above;
                the backend /seo/page/jobs-board/meta endpoint exists but
                doesn't know about the current filter/query, so useSeo
                remains the source of truth for this page. */}
            {/* Header */}
            <header className="bg-ink text-white sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2 shrink-0">
                        <Brand variant="light" size="h-7 sm:h-8" to={null} />
                    </Link>
                    <form
                        onSubmit={submitSearch}
                        className="flex-1 min-w-0 max-w-2xl flex items-center bg-white/10 hover:bg-white/15 transition-colors"
                    >
                        <span className="px-3 text-white/60">
                            <MagnifyingGlass size={14} weight="bold" />
                        </span>
                        <input
                            value={draftQ}
                            onChange={(e) => setDraftQ(e.target.value)}
                            placeholder="Search roles…"
                            data-testid="board-search-q"
                            className="flex-1 bg-transparent py-2.5 text-sm focus:outline-none placeholder:text-white/40 min-w-0"
                        />
                        <div className="hidden md:block border-l border-white/10 w-40">
                            <AutocompleteInput
                                testId="board-search-loc"
                                staticOptions={LOCATION_SUGGESTIONS}
                                value={draftLoc}
                                onChange={setDraftLoc}
                                placeholder="Location"
                                className="w-full bg-transparent px-3 py-2.5 text-sm focus:outline-none placeholder:text-white/40 text-white"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="board-search-submit"
                            className="bg-cobalt hover:bg-cobalt-dark px-4 py-2.5 text-xs font-bold uppercase tracking-wider"
                        >
                            Search
                        </button>
                    </form>
                    <div className="hidden sm:flex items-center gap-2 text-xs shrink-0">
                        {user?.role === "candidate" ? (
                            <Link to="/candidate" className="text-white/70 hover:text-white font-bold uppercase tracking-wider">
                                My portal
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/candidate/login"
                                    data-testid="jobs-board-candidate-login"
                                    className="text-white/70 hover:text-white font-bold uppercase tracking-wider"
                                >
                                    Sign in
                                </Link>
                                <Link
                                    to="/candidate/signup"
                                    data-testid="jobs-board-candidate-signup"
                                    className="bg-cobalt hover:bg-cobalt-dark text-white px-3 py-1.5 font-bold uppercase tracking-wider transition-colors"
                                >
                                    Sign up
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                {/* Page title + active filter chips */}
                <div className="flex items-end justify-between gap-3 mb-5">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-1">
                            Job board
                        </p>
                        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
                            {q || location
                                ? `Results${q ? ` for "${q}"` : ""}${location ? ` in ${location}` : ""}`
                                : "All open roles"}
                        </h1>
                        {jobs && (
                            <p className="text-sm text-[#525252] mt-1" data-testid="board-result-count">
                                {jobs.length} {jobs.length === 1 ? "role" : "roles"}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={() => setFiltersOpen((v) => !v)}
                        data-testid="board-toggle-filters"
                        className="lg:hidden border border-[#E5E5E5] hover:border-ink px-3 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 transition-colors"
                    >
                        <Funnel size={14} weight="bold" />
                        Filters{activeFilters ? ` (${activeFilters})` : ""}
                    </button>
                </div>

                {/* Filter chips (active) */}
                {activeFilters > 0 && (
                    <div className="flex flex-wrap gap-2 mb-5" data-testid="board-active-chips">
                        {q && (
                            <Chip onClear={() => setFilter("q", "")}>“{q}”</Chip>
                        )}
                        {location && (
                            <Chip onClear={() => setFilter("location", "")}>
                                <MapPin size={10} weight="bold" /> {location}
                            </Chip>
                        )}
                        {employment_type && (
                            <Chip onClear={() => setFilter("type", "")}>
                                <Briefcase size={10} weight="bold" /> {employment_type}
                            </Chip>
                        )}
                        {seniority && (
                            <Chip onClear={() => setFilter("seniority", "")}>
                                <UserCircle size={10} weight="bold" /> {seniority}
                            </Chip>
                        )}
                        {industry && (
                            <Chip onClear={() => setFilter("industry", "")}>
                                <Buildings size={10} weight="bold" /> {industry}
                            </Chip>
                        )}
                        <button
                            onClick={clearAll}
                            className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3] hover:text-ink underline underline-offset-2 ml-1"
                        >
                            Clear all
                        </button>
                        {user?.role === "candidate" && (
                            <button
                                onClick={saveSearch}
                                disabled={saving}
                                data-testid="board-save-search"
                                className="ml-auto bg-ink text-white hover:bg-cobalt px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                            >
                                {saving ? <Check size={11} weight="bold" /> : <BookmarkSimple size={11} weight="bold" />}
                                {saving ? "Saving…" : "Save search"}
                            </button>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 sm:gap-8">
                    {/* Sidebar filters */}
                    <aside
                        className={`${filtersOpen ? "block" : "hidden"} lg:block bg-white border border-[#E5E5E5] p-4 sm:p-5 h-fit lg:sticky lg:top-[68px]`}
                        data-testid="board-filters"
                    >
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-3">
                            Filters
                        </p>
                        <div className="space-y-4">
                            <FilterGroup
                                label="Industry"
                                value={industry}
                                options={INDUSTRIES}
                                onChange={(v) => setFilter("industry", v)}
                                testid="filter-industry"
                            />
                            <FilterGroup
                                label="Employment type"
                                value={employment_type}
                                options={TYPES}
                                onChange={(v) => setFilter("type", v)}
                                testid="filter-type"
                            />
                            <FilterGroup
                                label="Seniority"
                                value={seniority}
                                options={SENIORITIES}
                                onChange={(v) => setFilter("seniority", v)}
                                testid="filter-seniority"
                            />
                            <FilterGroup
                                label="Work mode"
                                value={work_mode}
                                options={WORK_MODES}
                                onChange={(v) => setFilter("work_mode", v)}
                                testid="filter-work_mode"
                            />
                        </div>
                    </aside>

                    {/* Results */}
                    <main>
                        {jobs == null ? (
                            <div className="text-sm text-[#525252]">Loading roles…</div>
                        ) : jobs.length === 0 ? (
                            <div className="border border-dashed border-[#E5E5E5] p-10 text-center bg-white">
                                <p className="text-sm text-[#525252]">
                                    No roles match these filters. Try widening your search.
                                </p>
                                <button
                                    onClick={clearAll}
                                    className="mt-3 bg-ink hover:bg-cobalt text-white px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors"
                                >
                                    Reset filters
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {jobs.map((j) => (
                                    <button
                                        key={j.id}
                                        onClick={() => navigate(`/jobs/${j.id}`)}
                                        data-testid={`board-job-${j.id}`}
                                        className="w-full text-left bg-white border border-[#E5E5E5] hover:border-ink p-4 sm:p-5 transition-colors group"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                {j.client?.name && (
                                                    <p className="text-[10px] font-bold uppercase tracking-wider text-cobalt mb-1 flex items-center gap-1">
                                                        <Buildings size={10} weight="bold" />
                                                        {j.client.name}
                                                    </p>
                                                )}
                                                <p className="font-heading text-lg font-bold tracking-tight leading-tight">
                                                    {j.title}
                                                </p>
                                                {j.description && (
                                                    <p className="text-sm text-[#525252] mt-1 line-clamp-2">
                                                        {j.description.replace(/<[^>]+>/g, "").slice(0, 180)}
                                                    </p>
                                                )}
                                                <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-[#525252]">
                                                    {(j.display_location || j.location) && (
                                                        <span className="inline-flex items-center gap-1">
                                                            <MapPin size={11} weight="bold" /> {j.display_location || j.location}
                                                        </span>
                                                    )}
                                                    {j.work_mode && (
                                                        <span
                                                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 font-bold uppercase tracking-wider text-[9px] ${
                                                                j.work_mode === "Remote"
                                                                    ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                                                    : j.work_mode === "Hybrid"
                                                                        ? "bg-violet-50 text-violet-700 border border-violet-100"
                                                                        : "bg-slate-50 text-slate-700 border border-slate-100"
                                                            }`}
                                                        >
                                                            {j.work_mode}
                                                        </span>
                                                    )}
                                                    {j.employment_type && (
                                                        <span className="inline-flex items-center gap-1">
                                                            <Briefcase size={11} weight="bold" /> {j.employment_type}
                                                        </span>
                                                    )}
                                                    {j.seniority && (
                                                        <span className="inline-flex items-center gap-1">
                                                            <UserCircle size={11} weight="bold" /> {j.seniority}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <ArrowRight
                                                size={18}
                                                weight="bold"
                                                className="text-[#A3A3A3] group-hover:text-cobalt transition-colors shrink-0 mt-1"
                                            />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
}

function Chip({ children, onClear }) {
    return (
        <span className="inline-flex items-center gap-1.5 bg-white border border-[#E5E5E5] px-2.5 py-1 text-xs text-[#525252]">
            {children}
            <button
                type="button"
                onClick={onClear}
                className="text-[#A3A3A3] hover:text-ink ml-1"
                aria-label="Remove filter"
            >
                <X size={10} weight="bold" />
            </button>
        </span>
    );
}

function FilterGroup({ label, value, options, onChange, testid }) {
    return (
        <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-2">
                {label}
            </p>
            <div className="flex flex-col gap-1">
                {options.map((opt) => (
                    <button
                        key={opt || "any"}
                        type="button"
                        data-testid={`${testid}-${opt || "any"}`}
                        onClick={() => onChange(opt)}
                        className={`text-left text-xs px-2 py-1.5 transition-colors ${
                            value === opt
                                ? "bg-ink text-white"
                                : "text-[#525252] hover:bg-[#F5F5F5] hover:text-ink"
                        }`}
                    >
                        {opt || "Any"}
                    </button>
                ))}
            </div>
        </div>
    );
}
